/* eslint-disable @typescript-eslint/no-use-before-define */

import {ArithmeticOperator, LogicalOperator, Property, TypeCastOperator} from '../models/enums/expression-enums';
import {ExpressionNode} from '../models/expression/expression-node';
import {OperandNode} from '../models/expression/operand-node';
import {OperatorNode} from '../models/expression/operator-node';
import {Operator} from '../models/types/expression-types';

export const isOperatorNode = (node: ExpressionNode): node is OperatorNode => {
  return node && (node as OperatorNode).operator !== undefined;
};

const isLogicalOperator = (operator: Operator): operator is LogicalOperator => {
  return operator === LogicalOperator.And || operator === LogicalOperator.Or;
};

const isArithmeticOperator = (operator: Operator): operator is ArithmeticOperator => {
  return Object.values(ArithmeticOperator).includes(operator as ArithmeticOperator);
};

const isTypeCastOperator = (operator: Operator): operator is TypeCastOperator => {
  return Object.values(TypeCastOperator).includes(operator as TypeCastOperator);
};

const isLogicalOperatorNode = (node: ExpressionNode): node is OperatorNode => {
  return isOperatorNode(node) && isLogicalOperator(node.operator);
};

export const isArithmeticOperatorNode = (node: ExpressionNode): node is OperatorNode => {
  return isOperatorNode(node) && isArithmeticOperator(node.operator);
};

export const isTypeCastOperatorNode = (node: ExpressionNode): node is OperatorNode => {
  return isOperatorNode(node) && isTypeCastOperator(node.operator);
};

export const isOperandNode = (node: ExpressionNode): node is OperandNode => {
  return node && (node as OperandNode).operand !== undefined;
};

export const isLiteralOperandNode = (node: ExpressionNode): node is OperandNode => {
  return isOperandNode(node) && node.operand.property === Property.Literal;
};

export const isPropertyTypeOperandNode = (node: ExpressionNode): node is OperandNode => {
  return isOperandNode(node) && node.operand.property !== Property.Literal;
};

const isCohortOperandNode = (node: ExpressionNode): node is OperandNode => {
  return isOperandNode(node) && [Property.Cohort, Property.PredefinedCohort].includes(node.operand.property);
};

export const isBehaviorOperandNode = (node: ExpressionNode): node is OperandNode => {
  return isOperandNode(node) && node.operand.property === Property.Behaviors;
};

export const onlyLeftAssociatedAndOperators = (node: ExpressionNode): boolean => {
  if (!isClauseRoot(node) && !isLiteralRoot(node) && isLogicalOperatorNode(node)) {
    if (
      node.operator === LogicalOperator.And &&
      ((isOperatorNode(node.right) && node.right.operator === LogicalOperator.Or) ||
        (isOperatorNode(node.left) && node.left.operator === LogicalOperator.Or))
    ) {
      return false;
    }

    return onlyLeftAssociatedAndOperators(node.left) && onlyLeftAssociatedAndOperators(node.right);
  }

  return true;
};

const isLiteralRoot = (node: ExpressionNode): boolean => {
  return (
    isOperatorNode(node) &&
    node.operator === LogicalOperator.And &&
    isOperatorNode(node.left) &&
    isOperandNode(node.right) &&
    node.right.operand.value === true
  );
};

export const isClauseRoot = (node: ExpressionNode): boolean => {
  return (
    isOperatorNode(node) &&
    node.operator === LogicalOperator.Or &&
    (isOperatorNode(node.left) || isOperandNode(node.left)) &&
    isOperandNode(node.right) &&
    node.right.operand.value === false
  );
};

const isSingleClauseLiteralRoot = (node: ExpressionNode): boolean => {
  return (
    isOperatorNode(node) && !isLogicalOperator(node.operator) && (!node.parent || isLogicalOperatorNode(node.parent))
  );
};

export const getLogicalOperators = (tree: ExpressionNode): LogicalOperator[] => {
  return tree.traverse<LogicalOperator, OperatorNode>(
    (node) => node.operator as LogicalOperator,
    (node) => isLogicalOperatorNode(node) && !isLiteralRoot(node) && !isClauseRoot(node),
    (node) => !isLiteralRoot(node) && !isClauseRoot(node),
  );
};

export const getLiteralTrees = (tree: ExpressionNode): OperatorNode[] => {
  return tree.traverse<OperatorNode, OperatorNode>((node) => node.left as OperatorNode, isLiteralRoot);
};

export const getClauseTrees = (tree: ExpressionNode): OperatorNode[] => {
  return tree.traverse<OperatorNode, OperatorNode>((node) => node.left as OperatorNode, isClauseRoot);
};

export const getSingleClauseLiteralTrees = (tree: ExpressionNode): OperatorNode[] => {
  return tree.traverse<OperatorNode, OperatorNode>((node) => node, isSingleClauseLiteralRoot);
};

export const getSingleClauseLogicalOperators = (tree: ExpressionNode): LogicalOperator[] => {
  return tree.traverse<LogicalOperator, OperatorNode>(
    (node) => node.operator as LogicalOperator,
    isLogicalOperatorNode,
    (node) => !isSingleClauseLiteralRoot(node),
  );
};

export const isCohortLiteralTree = (tree: ExpressionNode): boolean => {
  let node = tree;
  while (isOperatorNode(node)) {
    node = node.left;
  }

  return isCohortOperandNode(node);
};

export const isEventLiteralTree = (tree: ExpressionNode): boolean => {
  let node = tree;
  while (isOperatorNode(node)) {
    node = node.left;
  }

  return isBehaviorOperandNode(node);
};

export const joinExpressions = (expressionTrees: OperatorNode[], joinOperator: LogicalOperator): OperatorNode => {
  return expressionTrees
    .slice(1)
    .reduce((tree, subTree) => new OperatorNode(joinOperator, tree, subTree), expressionTrees[0]);
};
